import { useQuery } from "@apollo/client/react";
import { SyllableRankingDocument, SyllableRankingType } from "../../../generated/graphql";

interface SyllableTop10ListsProps {
  slideId: string;
  studentId?: string;
}

const getTrendIcon = (trend: string): string => {
  if (trend === "improving") return "📈";
  if (trend === "declining") return "📉";
  return "➡️";
};

export function SyllableTop10Lists({ slideId, studentId }: SyllableTop10ListsProps) {
  const { data: hardestData, loading: hardestLoading } = useQuery(SyllableRankingDocument, {
    variables: {
      slideId,
      studentId: studentId || null,
      sortBy: SyllableRankingType.Hardest,
      limit: 10,
    },
  });

  const { data: easiestData, loading: easiestLoading } = useQuery(SyllableRankingDocument, {
    variables: {
      slideId,
      studentId: studentId || null,
      sortBy: SyllableRankingType.Easiest,
      limit: 10,
    },
  });

  if (hardestLoading || easiestLoading) {
    return <div className="text-gray-600">Loading top 10 lists...</div>;
  }

  const hasHardestData = hardestData?.syllableRanking && hardestData.syllableRanking.length > 0;
  const hasEasiestData = easiestData?.syllableRanking && easiestData.syllableRanking.length > 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Hardest Syllables */}
      <div className="panel p-4 border-l-4 border-l-error-500">
        <h3 className="h5 mb-4">Top 10 Hardest Syllables</h3>
        {hasHardestData ? (
          <div className="space-y-2">
            {hardestData.syllableRanking.map((item, index) => (
              <div
                key={item.syllable}
                className="flex items-center justify-between panel p-3 border-l-2 border-l-error-500"
              >
                <div className="flex items-center space-x-3">
                  <span className="caption font-bold w-6">#{index + 1}</span>
                  <span className="text-lg font-semibold text-teal-400">{item.syllable}</span>
                  <span className="text-sm">{getTrendIcon(item.trend)}</span>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-secondary-200">
                    {item.averageScore.toFixed(1)}
                  </div>
                  <div className="caption">{item.attemptCount} attempts</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-600">Not enough data to show</div>
        )}
      </div>

      {/* Easiest Syllables */}
      <div className="panel p-4 border-l-4 border-l-success-500">
        <h3 className="h5 mb-4">Top 10 Easiest Syllables</h3>
        {hasEasiestData ? (
          <div className="space-y-2">
            {easiestData.syllableRanking.map((item, index) => (
              <div
                key={item.syllable}
                className="flex items-center justify-between panel p-3 border-l-2 border-l-success-500"
              >
                <div className="flex items-center space-x-3">
                  <span className="caption font-bold w-6">#{index + 1}</span>
                  <span className="text-lg font-semibold text-teal-400">{item.syllable}</span>
                  <span className="text-sm">{getTrendIcon(item.trend)}</span>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-secondary-200">
                    {item.averageScore.toFixed(1)}
                  </div>
                  <div className="caption">{item.attemptCount} attempts</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-600">Not enough data to show</div>
        )}
      </div>
    </div>
  );
}
